import { useCallback, useEffect, useState } from "react";
import { getTenantsByUid, getTenantsByEmail, getPropertyById } from "../helpers/firestoreService";
import { useAuthUser } from "./AuthProvider";
import { useRoleContext } from "../context/RoleContext";

/**
 * Every rental a tenant has actually accepted — `[{ tenantRecord, property }]`.
 * Shared by TenantHome (dashboard) and TenantMyRentals so both agree on what
 * counts as an active rental (pending/declined invites are excluded here;
 * those are surfaced separately as invitations on TenantMyRentals).
 */
export default function useMyRentals() {
  const user = useAuthUser();
  const { rentalsVersion } = useRoleContext() || {};
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const [byUid, byEmail] = await Promise.all([
        getTenantsByUid(user.uid),
        user.email ? getTenantsByEmail(user.email) : Promise.resolve([]),
      ]);
      const seen = new Set(byUid.map((r) => r.id));
      const merged = [...byUid, ...byEmail.filter((r) => !seen.has(r.id))];
      const active = merged.filter(
        (r) => r.propertyId && r.inviteStatus !== "pending" && r.inviteStatus !== "declined"
      );
      const enriched = await Promise.all(
        active.map(async (r) => ({ tenantRecord: r, property: await getPropertyById(r.propertyId) }))
      );
      setRentals(enriched);
    } catch (e) {
      console.error("Load rentals error:", e);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, user?.email]);

  useEffect(() => { load(); }, [load, rentalsVersion]);

  return { rentals, loading, reload: load };
}
